import { LoadingButton } from "@mui/lab";
import type { DialogProps, ListItemProps } from "@mui/material";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { getRoles, useAuth } from "@ssf/auth-components";
import { queryClient } from "@ssf/components";
import type { Document, Signature as SignatureType } from "@ssf/sign-worker";
import { CheckCircle, Clock, DotsVertical } from "mdi-material-ui";
import { enqueueSnackbar } from "notistack";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useChangeEmail, useSendReminder } from "../lib/signApi.js";
import { Signature, SignatureRemoveButton } from "./index.js";

export const SignatureItem = ({
  document,
  email,
  name,
  signature,
  open: defaultOpen,
  icon,
  documentApproved,
  ...props
}: {
  document?: Document;
  email?: string;
  name?: string;
  signature?: SignatureType;
  open?: boolean;
  icon?: ReactNode;
  documentApproved?: boolean;
} & ListItemProps) => {
  const { t } = useTranslation("SignatureItem");
  const { user, isFedAdmin } = useAuth();
  const [open, setOpen] = useState(defaultOpen);

  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [changeEmailDialog, setChangeEmailDialog] = useState(false);

  const { mutate: sendReminder, isPending: isReminderLoading } =
    useSendReminder({
      onSuccess(_data, variables) {
        enqueueSnackbar(t("reminderSuccess", variables), {
          variant: "success",
        });
        setAnchorEl(undefined);
      },
    });

  const content = (
    <>
      {icon ||
        (signature?.signedAt ? (
          documentApproved ? (
            <CheckCircle color="success" />
          ) : (
            <CheckCircle color="info" />
          )
        ) : (
          <Clock color="warning" />
        ))}
      <ListItemText sx={{ ml: 1 }}>{name}</ListItemText>
      <Box typography="body2">
        {signature?.signedAt
          ? new Date(signature.signedAt).toLocaleString("sv-SE")
          : t("unsigned")}
      </Box>
      {!signature?.signedAt && document && user && email && (
        <>
          <IconButton
            size="small"
            sx={{ ml: 1, mr: -1 }}
            onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
          >
            <DotsVertical fontSize="small" />
          </IconButton>
          <Menu
            open={!!anchorEl}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClose={() => setAnchorEl(undefined)}
          >
            <MenuItem
              disabled={isReminderLoading}
              onClick={() => sendReminder({ documentId: document.id, email })}
            >
              {t("sendReminder")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(undefined);
                setChangeEmailDialog(true);
              }}
            >
              {t("changeEmail")}
            </MenuItem>
          </Menu>

          <ChangeEmailDialog
            open={changeEmailDialog}
            onClose={() => setChangeEmailDialog(false)}
            document={document}
            email={email}
          />
        </>
      )}
    </>
  );

  return (
    <>
      <ListItem
        key={email}
        disablePadding={!!signature}
        secondaryAction={
          signature?.signedAt &&
          !documentApproved &&
          user &&
          (isFedAdmin ||
            (document &&
              getRoles(user).some(
                (role) =>
                  role === `club:${document.meta.club.id}` ||
                  role === `district:${document.meta.district.id}`,
              ))) &&
          email && <SignatureRemoveButton signatureId={signature.id} />
        }
        {...props}
      >
        {signature ? (
          <ListItemButton onClick={() => signature && setOpen(!open)}>
            {content}
          </ListItemButton>
        ) : (
          content
        )}
      </ListItem>
      <Collapse
        in={open}
        timeout="auto"
        sx={{
          img: {
            height: "auto",
            background: ({ palette }) => palette.grey[100],
          },
        }}
      >
        {signature && <Signature signature={signature} />}
      </Collapse>
    </>
  );
};

function ChangeEmailDialog({
  document,
  email,
  ...dialogProps
}: DialogProps & { document: Document; email: string }) {
  const { t } = useTranslation("SignatureItem");
  const handleClose = () => dialogProps.onClose?.({}, "escapeKeyDown");
  const [newEmail, setNewEmail] = useState(email);
  const { mutate: changeEmail, isPending: isChangeEmailLoading } =
    useChangeEmail({
      onSuccess(_data, variables) {
        handleClose();
        enqueueSnackbar(t("changeEmailSuccess", variables), {
          variant: "success",
        });
        queryClient.invalidateQueries();
      },
    });

  return (
    <Dialog {...dialogProps} PaperProps={{ sx: { minWidth: 300 } }}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          changeEmail({
            documentId: document.id,
            email,
            newEmail,
          });
        }}
      >
        <DialogTitle>{t("changeEmail")}</DialogTitle>
        <DialogContent>
          <TextField
            name="email"
            type="email"
            autoFocus
            margin="dense"
            label={t("emailLabel")}
            fullWidth
            defaultValue={email}
            onChange={({ target }) => setNewEmail(target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose()}>{t("cancel")}</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isChangeEmailLoading}
            disabled={
              "signers" in document.meta &&
              document.meta.signers.some(({ email }) => email === newEmail)
            }
          >
            {t("save")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
